import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  HelpCircle, X, Compass, Rocket, CheckCircle2, Circle, ArrowRight, Lightbulb,
} from "lucide-react";
import { getHelpForRoute, ONBOARDING_PASOS } from "../lib/help-content";
import { api, sucursalesApi, articulosConfigApi, whatsappApi } from "../lib/api";

/**
 * HelpDrawer — sistema de ayuda contextual.
 *  - Botón flotante (esquina inferior derecha) siempre visible
 *  - Drawer derecho con 2 pestañas:
 *      1) "Esta sección" — contenido específico según la ruta actual
 *      2) "Primeros pasos" — onboarding con auto-detección de progreso
 */
export function HelpDrawer() {
  const [abierto, setAbierto] = useState(false);
  const [tab, setTab] = useState<"seccion" | "inicio">("seccion");
  const [estadoOnboarding, setEstadoOnboarding] = useState<Record<string, any>>({});
  const location = useLocation();
  const ayuda = getHelpForRoute(location.pathname);

  // Cargar estado del onboarding cuando se abre el tab "inicio"
  useEffect(() => {
    if (!abierto || tab !== "inicio") return;
    const cargar = async () => {
      const estado: Record<string, any> = {};
      // Detectores ligeros (best-effort, swallow errors)
      try {
        const cats = await api<any[]>("/v1/categorias");
        estado.numCategorias = cats?.length ?? 0;
      } catch {}
      try {
        const us = await api<any[]>("/v1/users");
        estado.numUsuarios = us?.length ?? 0;
      } catch {}
      try {
        const p = await api<any[]>("/v1/pasarelas");
        estado.numPasarelasActivas = (p ?? []).filter((x: any) => x?.activo).length;
      } catch {}
      try {
        const b = await api<any>("/v1/branding");
        estado.tieneLogo = !!b?.logoUrl;
      } catch {}
      try {
        const r = await articulosConfigApi.list({ soloDelivery: true, limit: 1 });
        estado.numArticulosDelivery = r?.total ?? 0;
      } catch {}
      try {
        const wa = await whatsappApi.getConfig();
        estado.waConfigurado = !!wa?.activo;
      } catch {}
      try {
        const smtp = await api<any>("/v1/smtp-config");
        estado.smtpConfigurado = !!smtp?.host;
      } catch {}
      try {
        const sucs = await sucursalesApi.list();
        const primeraId = sucs?.[0]?.id;
        if (primeraId) {
          try {
            const zonas = await api<any[]>(`/v1/sucursales/${primeraId}/zonas-config`);
            estado.sucursalZonasConfiguradas = (zonas ?? []).some(
              (z) => z?.horarioSemanal && Object.keys(z.horarioSemanal).length > 0,
            );
          } catch {}
          try {
            const menuCfg = await api<any>(`/v1/menu-publico/sucursal/${primeraId}/config`);
            estado.numPdfs = Array.isArray(menuCfg?.pdfs) ? menuCfg.pdfs.length : 0;
          } catch {}
        }
      } catch {}
      setEstadoOnboarding(estado);
    };
    cargar();
  }, [abierto, tab]);

  // Atajo: tecla "?" abre la ayuda + evento global "open-help" (link del footer)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "?" && !abierto && !["INPUT", "TEXTAREA"].includes((e.target as HTMLElement)?.tagName ?? "")) {
        setAbierto(true);
      }
      if (e.key === "Escape" && abierto) setAbierto(false);
    };
    const onOpenHelp = () => setAbierto(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-help", onOpenHelp as EventListener);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-help", onOpenHelp as EventListener);
    };
  }, [abierto]);

  // Cambiar a tab "esta sección" cuando cambia la ruta
  useEffect(() => {
    if (abierto) setTab("seccion");
  }, [location.pathname]);

  const completados = ONBOARDING_PASOS.filter((p) => p.detectar?.(estadoOnboarding)).length;
  const totalEsenciales = ONBOARDING_PASOS.filter((p) => p.esencial).length;
  const completadosEsenciales = ONBOARDING_PASOS.filter((p) => p.esencial && p.detectar?.(estadoOnboarding)).length;
  const pctEsenciales = Math.round((completadosEsenciales / Math.max(totalEsenciales, 1)) * 100);

  return (
    <>
      {/* BOTÓN FLOTANTE */}
      <button
        onClick={() => setAbierto(true)}
        className="fixed bottom-6 right-6 z-40 size-12 sm:size-14 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center group"
        title="Ayuda (?)"
      >
        <HelpCircle className="size-6 sm:size-7" />
        <span className="absolute -top-1 -right-1 size-5 bg-amber-400 text-amber-900 text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
          ?
        </span>
        <span className="absolute right-full mr-3 px-3 py-1.5 rounded-lg bg-ink-800 text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          ¿Necesitás ayuda? Click acá
        </span>
      </button>

      {/* DRAWER */}
      {abierto && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setAbierto(false)}
            className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm animate-fade-in"
          />
          {/* Drawer */}
          <aside
            className="fixed top-0 right-0 z-50 h-full w-full sm:w-[480px] bg-white shadow-2xl flex flex-col animate-slide-in-right"
            role="dialog"
            aria-label="Ayuda"
          >
            {/* Header */}
            <header className="bg-gradient-to-r from-indigo-500 to-indigo-700 text-white p-5 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-bold text-lg flex items-center gap-2">
                  <Lightbulb className="size-5" /> Centro de ayuda
                </h2>
                <p className="text-xs opacity-90 mt-0.5">
                  Tip: tocá <kbd className="px-1.5 py-0.5 rounded bg-white/20 text-[10px]">?</kbd> en cualquier momento para abrir este panel.
                </p>
              </div>
              <button onClick={() => setAbierto(false)}
                className="p-2 rounded-full hover:bg-white/20 transition">
                <X className="size-5" />
              </button>
            </header>

            {/* TABS */}
            <div className="grid grid-cols-2 border-b border-ink-200 shrink-0">
              <button onClick={() => setTab("seccion")}
                className={`py-3 text-sm font-medium border-b-2 transition flex items-center justify-center gap-1.5 ${
                  tab === "seccion"
                    ? "border-indigo-500 text-indigo-700 bg-indigo-50/40"
                    : "border-transparent text-ink-500 hover:bg-ink-50"
                }`}>
                <Compass className="size-4" />
                Esta sección
              </button>
              <button onClick={() => setTab("inicio")}
                className={`py-3 text-sm font-medium border-b-2 transition flex items-center justify-center gap-1.5 relative ${
                  tab === "inicio"
                    ? "border-indigo-500 text-indigo-700 bg-indigo-50/40"
                    : "border-transparent text-ink-500 hover:bg-ink-50"
                }`}>
                <Rocket className="size-4" />
                Primeros pasos
                {completadosEsenciales < totalEsenciales && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">
                    {totalEsenciales - completadosEsenciales}
                  </span>
                )}
              </button>
            </div>

            {/* CONTENIDO */}
            <div className="flex-1 overflow-y-auto p-5">
              {tab === "seccion" ? (
                <SeccionContent ayuda={ayuda} />
              ) : (
                <OnboardingContent
                  estado={estadoOnboarding}
                  completados={completados}
                  pctEsenciales={pctEsenciales}
                  cerrar={() => setAbierto(false)}
                />
              )}
            </div>
          </aside>
        </>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Tab 1: Contenido contextual
// ────────────────────────────────────────────────────────────
function SeccionContent({ ayuda }: { ayuda: ReturnType<typeof getHelpForRoute> }) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-bold text-ink-900 text-xl mb-2">{ayuda.titulo}</h3>
        <p className="text-sm text-ink-600 leading-relaxed">{ayuda.intro}</p>
      </div>

      {ayuda.acciones.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-3">
            ¿Qué podés hacer acá?
          </h4>
          <ul className="space-y-3">
            {ayuda.acciones.map((a, i) => (
              <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-ink-50/50 border border-ink-100 hover:bg-ink-50 transition">
                <span className="text-2xl shrink-0">{a.emoji}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ink-800 text-sm">{a.titulo}</p>
                  <p className="text-xs text-ink-600 mt-0.5 leading-relaxed">{a.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {ayuda.tips && ayuda.tips.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-1.5">
            <Lightbulb className="size-4" /> Tips útiles
          </h4>
          <ul className="space-y-2 text-xs text-amber-900 leading-relaxed">
            {ayuda.tips.map((t, i) => (
              <li key={i} className="flex gap-2">
                <span className="shrink-0">•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Tab 2: Onboarding guiado
// ────────────────────────────────────────────────────────────
function OnboardingContent({
  estado, pctEsenciales, cerrar,
}: {
  estado: Record<string, any>;
  completados: number;
  pctEsenciales: number;
  cerrar: () => void;
}) {
  const esenciales = ONBOARDING_PASOS.filter((p) => p.esencial);
  const opcionales = ONBOARDING_PASOS.filter((p) => !p.esencial);

  return (
    <div className="space-y-5">
      {/* PROGRESO */}
      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-2xl p-4 border border-indigo-200">
        <div className="flex items-end justify-between mb-2">
          <div>
            <p className="text-xs uppercase tracking-wider text-indigo-700 font-semibold">
              Configuración inicial
            </p>
            <p className="text-2xl font-bold text-indigo-900 leading-none mt-1">
              {pctEsenciales}% <span className="text-sm font-normal text-indigo-600">completado</span>
            </p>
          </div>
          {pctEsenciales === 100 ? (
            <span className="text-3xl">🎉</span>
          ) : (
            <span className="text-3xl">🚀</span>
          )}
        </div>
        <div className="h-2 rounded-full bg-indigo-200 overflow-hidden mt-3">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-indigo-700 transition-all"
            style={{ width: `${pctEsenciales}%` }}
          />
        </div>
        {pctEsenciales < 100 && (
          <p className="text-xs text-indigo-700 mt-2">
            Completá los pasos esenciales abajo para tener el sistema listo para producción.
          </p>
        )}
      </div>

      {/* ESENCIALES */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-3 flex items-center gap-1.5">
          <span className="text-amber-500">⭐</span> Pasos esenciales
        </h4>
        <ul className="space-y-2">
          {esenciales.map((p) => (
            <PasoItem key={p.id} paso={p} hecho={!!p.detectar?.(estado)} cerrar={cerrar} />
          ))}
        </ul>
      </div>

      {/* OPCIONALES */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-3">
          Recomendados (opcionales)
        </h4>
        <ul className="space-y-2">
          {opcionales.map((p) => (
            <PasoItem key={p.id} paso={p} hecho={!!p.detectar?.(estado)} cerrar={cerrar} />
          ))}
        </ul>
      </div>

      <p className="text-center text-[11px] text-ink-400 pt-2">
        El estado se detecta automáticamente. Refrescá esta pestaña tras completar un paso.
      </p>
    </div>
  );
}

function PasoItem({ paso, hecho, cerrar }: { paso: typeof ONBOARDING_PASOS[number]; hecho: boolean; cerrar: () => void }) {
  return (
    <li>
      <Link
        to={paso.ruta}
        onClick={cerrar}
        className={`flex items-center gap-3 p-3 rounded-xl border transition group ${
          hecho
            ? "bg-emerald-50/50 border-emerald-200 hover:bg-emerald-50"
            : "bg-white border-ink-200 hover:border-indigo-300 hover:bg-indigo-50/30"
        }`}
      >
        {hecho ? (
          <CheckCircle2 className="size-6 text-emerald-500 shrink-0" />
        ) : (
          <Circle className="size-6 text-ink-300 shrink-0" />
        )}
        <span className="text-xl shrink-0">{paso.emoji}</span>
        <div className="min-w-0 flex-1">
          <p className={`font-semibold text-sm ${hecho ? "text-emerald-900 line-through decoration-emerald-400 decoration-1" : "text-ink-800"}`}>
            {paso.titulo}
          </p>
          <p className="text-xs text-ink-600 mt-0.5 leading-relaxed">{paso.descripcion}</p>
        </div>
        <ArrowRight className={`size-4 shrink-0 transition-transform group-hover:translate-x-0.5 ${hecho ? "text-emerald-500" : "text-ink-400"}`} />
      </Link>
    </li>
  );
}
